import React, { useEffect, useState } from "react";
import { X, FileSignature } from "lucide-react";
import { contractApi } from "../services/contractApi";
import { formatApiErrorDetail } from "../lib/api";
import { useLocalization } from "../hooks/useLocalization";
import { formatDate } from "../lib/date";

export const ContractGenerateDialog = ({ employee, onClose, onGenerated }) => {
  const { t } = useLocalization();
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    contractApi.listTemplates().then((r) => {
      setTemplates(r.data);
      if (r.data.length) setTemplateId(r.data[0].id);
    });
  }, []);

  const selected = templates.find((tpl) => tpl.id === templateId);

  const handleGenerate = async () => {
    setError("");
    setSubmitting(true);
    try {
      const { data } = await contractApi.generate(employee.id, templateId);
      onGenerated(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="generate-contract-dialog">
      <div className="bg-white rounded-2xl w-full max-w-lg p-6 shadow-xl">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <FileSignature className="text-blue-600" size={20} />
            <h3 className="text-lg font-bold text-gray-900">{t("contractGen.dialogTitle")}</h3>
          </div>
          <button data-testid="close-generate-contract-dialog" onClick={onClose} className="text-gray-500 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>

        {templates.length === 0 ? (
          <p className="text-sm text-gray-500" data-testid="no-active-template-message">{t("contractGen.noTemplate")}</p>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">{t("contractGen.selectTemplate")}</label>
              <select
                data-testid="contract-template-select"
                value={templateId}
                onChange={(e) => setTemplateId(e.target.value)}
                className="w-full px-4 py-2.5 rounded-lg border border-gray-300 text-sm"
              >
                {templates.map((tpl) => <option key={tpl.id} value={tpl.id}>{tpl.name}</option>)}
              </select>
            </div>

            {selected && (
              <div className="grid grid-cols-3 gap-3 text-xs text-gray-500 bg-gray-50 rounded-lg p-3">
                <div><p className="font-semibold text-gray-700">{t("contractGen.templateLanguage")}</p><p>{t("contractGen.bilingual")}</p></div>
                <div><p className="font-semibold text-gray-700">{t("contractGen.templateVersion")}</p><p>v{selected.version}</p></div>
                <div><p className="font-semibold text-gray-700">{t("contractGen.effectiveDate")}</p><p>{formatDate(selected.effective_date)}</p></div>
              </div>
            )}

            <div>
              <p className="text-xs font-semibold text-gray-500 mb-1">Employee</p>
              <p className="text-sm text-gray-800">{employee.legal_name} · {employee.employee_id} · {employee.position} · {employee.store_location}</p>
            </div>

            {error && <p className="text-sm text-red-500" data-testid="generate-contract-error">{error}</p>}

            <button
              data-testid="confirm-generate-contract-button"
              onClick={handleGenerate}
              disabled={submitting || !templateId}
              className="w-full py-2.5 rounded-full bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 active:scale-95 transition-all disabled:opacity-50"
            >
              {submitting ? t("common.loading") : t("contractGen.generate")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
